/**
 * @file cmd.ts
 * @description GCOME_AP 磁轴键盘 AP 协议命令与报告常量。
 */

export const GCOME_AP_REPORT = {
  defaultReportId: 0x00,
  defaultResponseReportId: 0x00,
  frameSize: 64,
  headerSize: 8,
  payloadSize: 56,
  hostFlag: 0x55,
  deviceFlag: 0xaa,
  timeoutMs: 50,
  maxRetries: 3,
} as const

/**
 * GCOME_AP 固件升级（IAP/ISP）命令定义。
 *
 * 协议细节参见 docs/果来微磁轴键盘固件升级协议.md：
 * 进入 Boot 在 APP 接口发 64 字节原始包；进 Boot 后 IAP 走 512 字节 HID 报告
 * （含 Report ID 时共 513 字节），有效载荷仍为命令头 + 32 字节数据。
 * 应答包前两个字节中的第二个字节（buffer[1]）不等于 0 表示校验失败/错误码。
 */
export const GCOME_AP_IAP = {
  /** 进入 Boot 命令（原始包，非 AP 帧） */
  enterBoot: 0x5f,
  /** ISP 编程命令（写入 flash） */
  writeFlash: 0x80,
  /** ISP 擦除命令（开始命令） */
  eraseFlash: 0x81,
  /** ISP 校验命令 */
  verifyFlash: 0x82,
  /** ISP 成功标志 */
  success: 0x83,
  /** ISP 结束标志 */
  end: 0x84,
  /**
   * APP 态进 Boot 包长（64 字节 Output，与 AP 控制口一致）。
   * BusHound：VT68 Keyboard EP OUT `5f 06 ...`。
   */
  enterBootPacketSize: 64,
  /**
   * Boot 态 IAP HID 报告长度（512 字节；WebHID 另加 Report ID 0 → 文档称 513）。
   * BusHound：`0x19F5:0xAF7A` Volt* Upgrader，UsagePage=0xFF00 Usage=0x01。
   */
  packetSize: 512,
  /** 单包有效数据长度（32 字节） */
  dataChunk: 32,
  /** 协议头长度：命令码 + 数据长度 + 4 字节地址 */
  headerSize: 6,
  /** 擦除命令头长度 */
  eraseHeaderLength: 0x07,
  /** 设备类型：0 键盘设备，1 RF 设备 */
  deviceTypeKeyboard: 0x00,
  deviceTypeRf: 0x01,
} as const

export const GCOME_AP_CMD = {
  fastStart: 0x01,
  fastEnd: 0x02,
  getInfo: 0x03,
  getBase: 0x04,
  getFunc: 0x05,
  setFunc: 0x06,
  getDefaultKeyMatrix: 0x07,
  getUseKeyMatrix: 0x08,
  setUseKeyMatrix: 0x09,
  getLedDefine: 0x0a,
  setLedDefine: 0x0b,
  getMacro: 0x0c,
  setMacro: 0x0d,
  switchOnboardProfile: 0x0e,
  getWirelessDeviceLink: 0x0f,
  getJoystick: 0x10,
  setJoystick: 0x11,
  getAppDefine: 0xf1,
  setAppDefine: 0xf2,
  getKeyTriggerTravel: 0xa0,
  setKeyTriggerTravel: 0xa1,
  getDksInfo: 0xa2,
  setDksInfo: 0xa3,
  getMtKeyInfo: 0xa4,
  setMtKeyInfo: 0xa5,
  getTglKeyInfo: 0xa6,
  setTglKeyInfo: 0xa7,
  startCalibration: 0xa8,
  endCalibration: 0xa9,
  getCalibrationInfo: 0xaa,
  startJoystickCalibration: 0xac,
  endJoystickCalibration: 0xad,
  lightingSyncDownload: 0xdd,
  lightingSyncUpload: 0xde,
  factoryReset: 0xee,
} as const

export type GcomeApCommand = (typeof GCOME_AP_CMD)[keyof typeof GCOME_AP_CMD]

export const GCOME_AP_DANGEROUS_COMMANDS = new Set<number>([
  GCOME_AP_CMD.setFunc,
  GCOME_AP_CMD.setUseKeyMatrix,
  GCOME_AP_CMD.setLedDefine,
  GCOME_AP_CMD.setMacro,
  GCOME_AP_CMD.switchOnboardProfile,
  GCOME_AP_CMD.setAppDefine,
  GCOME_AP_CMD.setKeyTriggerTravel,
  GCOME_AP_CMD.setDksInfo,
  GCOME_AP_CMD.setMtKeyInfo,
  GCOME_AP_CMD.setTglKeyInfo,
  GCOME_AP_CMD.startCalibration,
  GCOME_AP_CMD.endCalibration,
  GCOME_AP_CMD.startJoystickCalibration,
  GCOME_AP_CMD.endJoystickCalibration,
  GCOME_AP_CMD.lightingSyncDownload,
  GCOME_AP_CMD.factoryReset,
])

export const GCOME_AP_COMMAND_LABELS: Record<number, string> = {
  [GCOME_AP_CMD.fastStart]: '快速通讯开始',
  [GCOME_AP_CMD.fastEnd]: '快速通讯结束',
  [GCOME_AP_CMD.getInfo]: 'GetInfo',
  [GCOME_AP_CMD.getBase]: 'GetBase',
  [GCOME_AP_CMD.getFunc]: 'GetFunc',
  [GCOME_AP_CMD.setFunc]: 'SetFunc',
  [GCOME_AP_CMD.getDefaultKeyMatrix]: 'GetDefaultKeyMatrix',
  [GCOME_AP_CMD.getUseKeyMatrix]: 'GetUseKeyMatrix',
  [GCOME_AP_CMD.setUseKeyMatrix]: 'SetUseKeyMatrix',
  [GCOME_AP_CMD.getLedDefine]: 'GetLedDefine',
  [GCOME_AP_CMD.setLedDefine]: 'SetLedDefine',
  [GCOME_AP_CMD.getMacro]: 'GetMacro',
  [GCOME_AP_CMD.setMacro]: 'SetMacro',
  [GCOME_AP_CMD.switchOnboardProfile]: '软件切换板载',
  [GCOME_AP_CMD.getWirelessDeviceLink]: 'Get_2.4G_DevLink',
  [GCOME_AP_CMD.getJoystick]: 'GetJoystick（手柄参数）',
  [GCOME_AP_CMD.setJoystick]: 'SetJoystick（手柄参数）',
  [GCOME_AP_CMD.getAppDefine]: 'Get_app_define',
  [GCOME_AP_CMD.setAppDefine]: 'Set_app_define',
  [GCOME_AP_CMD.getKeyTriggerTravel]: 'Get key trigger travel',
  [GCOME_AP_CMD.setKeyTriggerTravel]: 'Set key trigger travel',
  [GCOME_AP_CMD.getDksInfo]: 'Get DKS info',
  [GCOME_AP_CMD.setDksInfo]: 'Set DKS info',
  [GCOME_AP_CMD.getMtKeyInfo]: 'Get MT key info',
  [GCOME_AP_CMD.setMtKeyInfo]: 'Set MT key info',
  [GCOME_AP_CMD.getTglKeyInfo]: 'Get TGL key info',
  [GCOME_AP_CMD.setTglKeyInfo]: 'Set TGL key info',
  [GCOME_AP_CMD.startCalibration]: 'Start calibration',
  [GCOME_AP_CMD.endCalibration]: 'End calibration',
  [GCOME_AP_CMD.getCalibrationInfo]: 'Get calibration info',
  [GCOME_AP_CMD.startJoystickCalibration]: 'Start joystick calibration',
  [GCOME_AP_CMD.endJoystickCalibration]: 'End joystick calibration',
  [GCOME_AP_CMD.lightingSyncDownload]: '灯效同步下载',
  [GCOME_AP_CMD.lightingSyncUpload]: '灯效同步上传',
  [GCOME_AP_CMD.factoryReset]: '恢复出厂设置',
}
